<template>
  <collapsible-section class='_pt0 _pb0' :collapsed='false'>
    <template #headerText>
      <p class='color-leaf'>Personal Information</p>
    </template>
    <template #collapsibleSection>
      <form>
        <async-loader v-if='memberDetailsLoading'/>
        <div v-else container>

          <div grid="row wrap">
            <div column="3" v-if="$isInternalUser">
              <app-input
                :disabled="memberLocked"
                v-model="$v.internalValue.fileBoundId.$model"
                :required="true"
                :errorMessage="fileBoundIdError"
                :invalid="$v.internalValue.fileBoundId.$invalid"
                valueKey="fileBoundId"
                label="Filebound ID:"
                @input="emitInternalValue"
              />
            </div>
            <div column="3">
              <client-health-id
                :enable-mask="ssnMatchesClientHealthId($v.internalValue.ssn.$model, $v.internalValue.clientHealthId.$model)"
                :disabled="memberLocked"
                v-model.trim="$v.internalValue.clientHealthId.$model"
                :required="memberIdSource === 'CS'"
                :errorMessage="clientHealthIdError"
                :invalid="$v.internalValue.clientHealthId.$invalid"
                valueKey="clientId"
                label="Client's Member ID:"
                @input="emitInternalValue"
              />
            </div>
          </div>
          <div grid="row wrap">
            <div column="s-6 m-4 l-4">
              <app-input
                :disabled='memberLocked'
                v-model='$v.internalValue.firstName.$model'
                :required='true'
                :errorMessage="firstNameError"
                :invalid="$v.internalValue.firstName.$invalid"
                valueKey='firstName'
                label='First Name:'
                @input='emitInternalValue'/>
            </div>
            <div column="s-6 m-2 l-2">
              <app-input
                :disabled='memberLocked'
                v-model='$v.internalValue.middleInitial.$model'
                :errorMessage="middleInitialError"
                :invalid="$v.internalValue.middleInitial.$invalid"
                valueKey='middleInitial'
                label='Middle Initial:'
                @input='emitInternalValue'
                />
            </div>
            <div column="s-6 m-4 l-4">
              <app-input
                :disabled='memberLocked'
                v-model='$v.internalValue.lastName.$model'
                :invalid="$v.internalValue.lastName.$invalid"
                valueKey='lastName'
                :required='true'
                :errorMessage="lastNameError"
                label='Last Name:'
                @input='emitInternalValue'/>
            </div>
            <div column="s-6 m-2 l-2">
              <app-input
                :disabled='memberLocked'
                v-model='$v.internalValue.suffix.$model'
                :invalid="$v.internalValue.suffix.$invalid"
                :errorMessage="suffixError"
                valueKey='suffix'
                label='Suffix:'
                @input='emitInternalValue'/>
            </div>
          </div>
          <div grid="row wrap">
            <div v-if="isDependent" column class="_mtxs">
              <app-select
                :disabled='memberLocked'
                v-model="$v.internalValue.relationship.$model"
                :required="true"
                :errorMessage="relationshipError"
                :invalid="$v.internalValue.relationship.$invalid"
                :options="relationshipOptions"
                name="relationship"
                label="relationship:"
                @input='emitInternalValue'
              />
            </div>
            <div column class="_mtxs">
              <app-select
                :disabled='memberLocked'
                v-model='$v.internalValue.gender.$model'
                :required='true'
                :errorMessage="genderError"
                :options="genderOptions"
                :invalid="$v.internalValue.gender.$invalid"
                valueKey='gender'
                label='Gender:'
                @input='emitInternalValue'
                />
            </div>
            <div column class="_mtxs">
              <app-date-input
                v-model='$v.internalValue.dob.$model'
                :required='true'
                :errorMessage="dobError"
                :invalid="$v.internalValue.dob.$invalid"
                valueKey='memberDob'
                label='Date of Birth:'
                @input='emitInternalValue'
              />
            </div>
            <div column class="_mtxs">
              <app-input
                :disabled='memberLocked'
                v-model='$v.internalValue.ssn.$model'
                :invalid="$v.internalValue.ssn.$invalid"
                :errorMessage="ssnError"
                type="password"
                valueKey='ssn'
                label='SSN:'
                @input='emitInternalValue'
              />
            </div>
          </div>
        </div>
      </form>
    </template>
  </collapsible-section>
</template>

<script>
  import AppInput from '@atoms/Input'
  import AppSelect from '@atoms/Select'
  import AppDateInput from '@atoms/DateInput'
  import AsyncLoader from '@molecules/AsyncLoader'
  import CollapsibleSection from '@sections/CollapsibleSection'

  import validationMessages from '@mixins/validationMessagesInternalValue'
  import SelectOptions from '@mixins/SelectOptions'
  import formReady from '@mixins/formReady'

  import { required, requiredIf, maxLength, alphaNum } from 'vuelidate/lib/validators'
  import { fourOrNineDigitsLong, nameAlphaNumeric, updateValues, relationshipCodeToGenderCode, fileBoundIdValidation } from '@/utils'

  import ClientHealthId from './ClientHealthId'
  import clientHealthId from '@mixins/clientHealthId'

  export default {
    mixins: [
      validationMessages,
      formReady,
      clientHealthId,
      SelectOptions
    ],
    components: {
      ClientHealthId,
      CollapsibleSection,
      AppInput,
      AppSelect,
      AppDateInput,
      AsyncLoader
    },
    props: {
      isDependent: {
        type: Boolean,
        default: false
      },
      memberIdSource: {
        type: String,
        default: ''
      },
      memberDetailsLoading: {
        type: Boolean,
        default: false
      },
      memberLocked: {
        type: Boolean,
        default: false
      },
      value: {
        type: Object,
        default: function() {
          return {
            firstName: '',
            middleInitial: '',
            lastName: '',
            gender: '',
            dob: '',
            ssn: '',
            clientHealthId: '',
            relationship: '',
            fileBoundId: ''
          }
        }
      }
    },
    data() {
      return {
        internalValue: {
          firstName: this.value.firstName || '',
          middleInitial: this.value.middleInitial || '',
          lastName: this.value.lastName || '',
          suffix: this.value.suffix || '',
          gender: this.value.gender || '',
          dob: this.value.dob || '',
          ssn: this.value.ssn || '',
          clientHealthId: this.value.clientHealthId || '',
          relationship: this.value.relationship || '',
          fileBoundId: this.value.fileBoundId || ''
        },
        origionalSSN: this.value.ssn || ''
      }
    },
    methods: {
      emitInternalValue() {
        this.$emit('input', this.internalValue)
      }
    },
    validations: {
      internalValue: {
        firstName: {
          required,
          maxLength: maxLength(15),
          nameAlphaNumeric
        },
        middleInitial: {
          maxLength: maxLength(1)
        },
        lastName: {
          required,
          maxLength: maxLength(35),
          nameAlphaNumeric
        },
        dob: {
          required
        },
        gender: {
          required
        },
        suffix: {
          maxLength: maxLength(10)
        },
        ssn: {
          fourOrNineDigitsLong(value) {
            // client request that invalid SSNs sent by API should not cause the SSN
            // validation to fail, but if the SSN is changed on this screen is should be valid
            const strippedSsn = value.replace(/-/g, '')
            const origStrippedSsn = this.origionalSSN.replace(/-/g, '')

            if (strippedSsn != origStrippedSsn) {
              return fourOrNineDigitsLong(value)
            }

            return true
          }
        },
        clientHealthId: {
          required: requiredIf(function() {
            return this.memberIdSource === 'CS'
          }),
          maxLength: maxLength(15),
          alphaNum
        },
        fileBoundId: {
          required: requiredIf(function() {
            return this.$isInternalUser
          }),
          maxLength: maxLength(15),
          fileBoundIdValidation
        },
        relationship: {
          required: requiredIf(function() {
            return this.isDependent
          })
        }
      }
    },
    watch: {
      value(newValue) {
        updateValues(this.internalValue, newValue)
      },
      'internalValue.relationship'(newRelationship) {
        const genderCode = relationshipCodeToGenderCode(newRelationship)
        if (genderCode) {
          this.internalValue.gender = genderCode

          this.emitInternalValue()
        }
      }
    }
  }
</script>
